'use client';
import React from 'react';
import Link from 'next/link';
import PublicNavbar from '@/app/landing/components/PublicNavbar';
import PublicFooter from '@/app/landing/components/PublicFooter';


const coverageItems = [
  {
    icon: '📦',
    title: 'Lost Parcel Coverage',
    desc: 'If your parcel is confirmed lost during transit, MuyuRide covers up to the declared value of your item — no lengthy claims process.',
    color: 'from-blue-50 to-white border-blue-100',
    accent: 'text-blue-600',
  },
  {
    icon: '💥',
    title: 'Damage in Transit',
    desc: 'Accidental damage during handling or transport is covered. Fragile items, electronics, and valuables are all eligible for damage claims.',
    color: 'from-amber-50 to-white border-amber-100',
    accent: 'text-amber-600',
  },
  {
    icon: '🏢',
    title: 'Storage Facility Incidents',
    desc: 'Items stored at MuyuRide-verified storage facilities are covered against theft, fire, and water damage during the booking period.',
    color: 'from-purple-50 to-white border-purple-100',
    accent: 'text-purple-600',
  },
  {
    icon: '🚫',
    title: 'Non-Delivery Protection',
    desc: 'If a traveler fails to deliver your parcel and cannot be reached, the insurance fund helps recover your declared item value.',
    color: 'from-red-50 to-white border-red-100',
    accent: 'text-red-600',
  },
  {
    icon: '⚡',
    title: 'Fast Claims Processing',
    desc: 'Claims are reviewed within 48 hours. Approved claims are paid out within 5 business days directly to your M-Pesa or bank account.',
    color: 'from-green-50 to-white border-green-100',
    accent: 'text-green-600',
  },
  {
    icon: '🔍',
    title: 'Dispute-Backed Resolution',
    desc: 'Every insurance claim is backed by MuyuRide\'s dispute resolution system — evidence, tracking data, and delivery records are all reviewed.',
    color: 'from-sky-50 to-white border-sky-100',
    accent: 'text-sky-600',
  },
];

const exclusions = [
  'Prohibited or illegal items (as listed in our Terms of Service)',
  'Items with undeclared or inflated values at time of order creation',
  'Damage caused by improper packaging by the sender',
  'Perishable goods that spoil due to natural causes',
  'Items not covered under the order\'s declared parcel category',
  'Claims submitted more than 7 days after confirmed delivery date',
];

const faqs = [
  {
    q: 'Is the insurance fee mandatory?',
    a: 'Yes. The 1.5% insurance fee is automatically applied to all luggage and delivery orders on MuyuRide. It ensures every sender and storage customer is protected without needing to opt in separately.',
  },
  {
    q: 'How is the 1.5% calculated?',
    a: 'The insurance fee is 1.5% of your offered/agreed delivery price. For example, if your delivery price is KES 1,000, the insurance fee is KES 15. This is added to your total payment at checkout.',
  },
  {
    q: 'How do I file a claim?',
    a: 'Go to Active Deliveries → open the affected delivery → click "Open Dispute". Select "Insurance Claim" as the dispute type. Provide photos, a description, and the declared value. Our team reviews within 48 hours.',
  },
  {
    q: 'What is the maximum insurance fee per delivery?',
    a: 'The insurance fee is capped at KES 10,000 per delivery per month. This means no matter how large your order value, you will never pay more than KES 10,000 in insurance fees for a single delivery in a given month.',
  },
  {
    q: 'Does the insurance cover storage bookings?',
    a: 'Yes. The 1.5% insurance fee also applies to storage bookings. Items stored at verified MuyuRide facilities are covered against theft, fire, and water damage during the booking period.',
  },
  {
    q: 'Who manages the insurance fund?',
    a: 'MuyuRide manages the insurance fund internally. All collected insurance fees are held in a dedicated reserve account used exclusively for approved customer claims and genuine loss payouts.',
  },
];

export default function InsurancePage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#0F2A1A] to-[#0F172A]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#16A34A]/20 blur-[120px]" />
          <div className="absolute top-1/2 -right-40 w-[400px] h-[400px] rounded-full bg-[#38BDF8]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#16A34A]/15 border border-[#16A34A]/30 text-green-300 text-sm font-semibold">
            <span className="text-xl">🛡️</span>
            MuyuRide Delivery Insurance
          </div>
          <h1 className="font-heading text-4xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            Every Delivery,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#16A34A] via-[#4ADE80] to-[#38BDF8]">
              Insured & Protected.
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            MuyuRide automatically applies a <strong className="text-white">1.5% insurance fee</strong> on all delivery and storage payments. This small fee creates a dedicated fund to cover genuine losses — so you never have to worry about what happens if something goes wrong.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl bg-[#16A34A] text-white font-heading font-bold hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_24px_rgba(22,163,74,0.4)]"
            >
              Send a Parcel — Insured
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white font-heading font-bold hover:bg-white/15 transition-all duration-300"
            >
              File a Claim
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>
      {/* How the fee works */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest mb-3">
              How It Works
            </span>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A]">Simple, automatic, transparent.</h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">No sign-up required. No forms to fill. Every order is automatically covered the moment you pay.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { step: '01', icon: '📝', title: 'Create Your Order', desc: 'Post your parcel or book storage. Declare the item value accurately — this determines your coverage amount.' },
              { step: '02', icon: '💳', title: '1.5% Added at Checkout', desc: 'The insurance fee is automatically calculated and added to your total. For KES 1,000 delivery, that\'s just KES 15.' },
              { step: '03', icon: '🛡️', title: 'Covered from Pickup to Delivery', desc: 'Your parcel is insured the moment the traveler picks it up until you confirm delivery. Storage is covered for the full booking period.' },
            ]?.map((item, i) => (
              <div key={item?.step} className="relative rounded-2xl p-6 bg-gradient-to-br from-green-50 to-white border border-green-100 hover:shadow-lg transition-all duration-300">
                {i < 2 && (
                  <div className="hidden md:block absolute top-10 -right-3 z-10 text-slate-300 text-xl">→</div>
                )}
                <div className="text-4xl font-black text-slate-200 mb-3">{item?.step}</div>
                <div className="text-3xl mb-3">{item?.icon}</div>
                <h3 className="font-heading font-extrabold text-[#0F172A] mb-2">{item?.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>

          {/* Fee example */}
          <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0F2A1A] p-8 text-white">
            <h3 className="font-heading text-xl font-extrabold mb-6 text-center">Example Payment Breakdown</h3>
            <div className="max-w-sm mx-auto space-y-3">
              {[
                { label: 'Offered delivery price', value: 'KES 1,000', highlight: false },
                { label: 'Service fee (15%)', value: 'KES 150', highlight: false },
                { label: 'Insurance fee (1.5%)', value: 'KES 15', highlight: true },
                { label: 'Total charged', value: 'KES 1,165', highlight: false, bold: true },
              ]?.map((row) => (
                <div key={row?.label} className={`flex justify-between items-center py-2 ${row?.bold ? 'border-t border-white/20 pt-3 font-bold text-lg' : 'text-sm'}`}>
                  <span className={row?.highlight ? 'text-green-300 font-semibold' : 'text-slate-300'}>{row?.label}</span>
                  <span className={row?.highlight ? 'text-green-300 font-bold' : row?.bold ? 'text-white' : 'text-white'}>{row?.value}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-4">Traveler earns KES 850 after the 15% service fee. The KES 15 insurance fee goes to the MuyuRide Insurance Reserve Fund.</p>
          </div>
        </div>
      </section>
      {/* What's covered */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest mb-3">
              Coverage Details
            </span>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A]">What&apos;s covered by MuyuRide Insurance?</h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">Your 1.5% fee covers a wide range of genuine loss scenarios — from lost parcels to storage incidents.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {coverageItems?.map((item) => (
              <div key={item?.title} className={`rounded-2xl p-6 bg-gradient-to-br ${item?.color} border hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5`}>
                <div className="text-3xl mb-4">{item?.icon}</div>
                <h3 className={`font-heading font-extrabold text-[#0F172A] mb-2`}>{item?.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Exclusions */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-sm font-bold uppercase tracking-widest mb-3">
              Exclusions
            </span>
            <h2 className="font-heading text-2xl lg:text-3xl font-extrabold text-[#0F172A]">What is NOT covered</h2>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto">To keep the insurance fund fair and sustainable, the following are excluded from coverage.</p>
          </div>
          <div className="rounded-2xl border border-red-100 bg-red-50/50 p-6 space-y-3">
            {exclusions?.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest mb-3">
              FAQ
            </span>
            <h2 className="font-heading text-3xl font-extrabold text-[#0F172A]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs?.map((faq) => (
              <div key={faq?.q} className="rounded-2xl bg-white border border-slate-100 p-6 hover:shadow-md transition-shadow">
                <h3 className="font-heading font-bold text-[#0F172A] mb-2">{faq?.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq?.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#16A34A] via-[#15803D] to-[#16A34A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#0F172A]/20 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="text-5xl">🛡️</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-white">Ship with confidence.</h2>
          <p className="text-green-100 text-lg max-w-xl mx-auto">Every MuyuRide delivery is automatically insured. No extra steps — just peace of mind from pickup to delivery.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white text-[#16A34A] font-heading font-extrabold hover:bg-green-50 transition-all duration-300 shadow-[0_4px_24px_rgba(0,0,0,0.15)]"
            >
              Send a Parcel Now →
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/10 border border-white/30 text-white font-heading font-bold hover:bg-white/15 transition-all duration-300"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
